/**
 * settings-overlay.ts — Settings screen (v2M4).
 *
 * Tabbed full-screen overlay; v2M4 ships only the Gameplay tab. Other
 * tabs (Audio / Graphics / Controls / Accessibility / Interface per
 * UI_UX §10) land alongside their backing systems — gating principle:
 * no tab without real controls.
 *
 * Edit-then-Save model — the overlay holds a local working copy of the
 * settings; Save persists to storage, Cancel discards. Reachable from
 * both the main-menu Settings button and the in-game-menu Settings
 * button; same overlay either way.
 */

import {
  USER_SPEED_LADDER,
  type UserSpeed,
} from '../game/speed-controller';
import {
  type GameplaySettings,
  type Settings,
} from '../settings/types';
import { getAudioEngine } from '../audio';

export interface SettingsOverlayCallbacks {
  /** Current persisted settings (the overlay seeds its working copy from this). */
  current: Settings;
  /** Save was clicked. Caller persists and closes. */
  onSave(next: Settings): void;
  /** Cancel was clicked. Caller closes without persisting. */
  onCancel(): void;
}

export interface SettingsOverlayHandle {
  destroy(): void;
}

/**
 * Deep-clone a Settings record so the overlay's working copy is
 * isolated from the persisted one. Settings are flat enough that a
 * per-sub-object spread is sufficient.
 */
function cloneSettings(s: Settings): Settings {
  return {
    schemaVersion: s.schemaVersion,
    gameplay: { ...s.gameplay },
    audio: { ...s.audio },
  };
}

export function mountSettingsOverlay(
  parent: HTMLElement,
  callbacks: SettingsOverlayCallbacks,
): SettingsOverlayHandle {
  // Working copy. Never mutates callbacks.current; on Save, the working
  // copy is what we hand back.
  const working: Settings = cloneSettings(callbacks.current);

  const overlay = document.createElement('div');
  overlay.id = 'settings-overlay';
  Object.assign(overlay.style, {
    position: 'fixed',
    inset: '0',
    background: 'rgba(5, 6, 10, 0.92)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '1100',
    fontFamily:
      'Inter, system-ui, -apple-system, "Segoe UI", sans-serif',
    color: '#D7DEEA',
  } as Partial<CSSStyleDeclaration>);

  const card = document.createElement('div');
  Object.assign(card.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    minWidth: '520px',
    maxWidth: '640px',
    padding: '28px 32px',
    background: '#0E131C',
    border: '1px solid #2A3344',
    borderRadius: '2px',
  } as Partial<CSSStyleDeclaration>);
  overlay.appendChild(card);

  const heading = document.createElement('div');
  heading.textContent = 'SETTINGS';
  Object.assign(heading.style, {
    fontSize: '14px',
    letterSpacing: '0.18em',
    color: '#7E8799',
    marginBottom: '6px',
  } as Partial<CSSStyleDeclaration>);
  card.appendChild(heading);

  // --- Tab strip --------------------------------------------------------
  // v2M8.8 — Audio tab joins Gameplay. Other tabs (Graphics / Controls /
  // Accessibility / Interface per UI_UX §10) gate on their backing
  // systems landing.
  const tabStrip = document.createElement('div');
  Object.assign(tabStrip.style, {
    display: 'flex',
    gap: '8px',
    borderBottom: '1px solid #2A3344',
    paddingBottom: '8px',
    marginBottom: '8px',
  } as Partial<CSSStyleDeclaration>);
  card.appendChild(tabStrip);

  const gameplayTab = makeTabButton('Gameplay', true);
  const audioTab = makeTabButton('Audio', false);
  tabStrip.appendChild(gameplayTab);
  tabStrip.appendChild(audioTab);

  // --- Gameplay pane ----------------------------------------------------
  const gameplayPane = document.createElement('div');
  Object.assign(gameplayPane.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
    padding: '4px 0',
  } as Partial<CSSStyleDeclaration>);
  card.appendChild(gameplayPane);

  gameplayPane.appendChild(makeSpeedRadioRow(working.gameplay));
  gameplayPane.appendChild(
    makeToggleRow(
      'Confirm Exit',
      'Show a confirmation prompt before quitting.',
      working.gameplay.confirmExit,
      (v) => {
        working.gameplay.confirmExit = v;
      },
    ),
  );
  gameplayPane.appendChild(
    makeToggleRow(
      'Confirm Return to Main Menu',
      'Show a confirmation prompt before discarding the current game.',
      working.gameplay.confirmReturnToMainMenu,
      (v) => {
        working.gameplay.confirmReturnToMainMenu = v;
      },
    ),
  );
  // v3M19.3 — Combat VFX sub-section. First entry is the floating-
  // damage-numbers toggle; future combat-VFX preferences (tracer
  // density, destruction-fade duration, etc.) land alongside.
  gameplayPane.appendChild(makeSectionHeader('Combat VFX'));
  gameplayPane.appendChild(
    makeToggleRow(
      'Show damage numbers',
      'Display floating damage numbers (and MISS tags) over ships during combat.',
      working.gameplay.showDamageNumbers,
      (v) => {
        working.gameplay.showDamageNumbers = v;
      },
    ),
  );

  // --- Audio pane (v2M8.8) ---------------------------------------------
  // Live-preview: each slider's onInput pushes the working volumes into
  // the engine immediately so the player can hear the change without
  // committing. Cancel restores the persisted values via the onCancel
  // path in main.ts (it doesn't touch ctx.settings, but the engine
  // would still hold the previewed values; that's acceptable for v1
  // since the next Settings open re-reads from persisted ctx.settings).
  const audioPane = document.createElement('div');
  Object.assign(audioPane.style, {
    display: 'none',
    flexDirection: 'column',
    gap: '14px',
    padding: '4px 0',
  } as Partial<CSSStyleDeclaration>);
  card.appendChild(audioPane);
  const audioLivePreview = (): void => {
    getAudioEngine().applyVolumes(working.audio);
  };
  audioPane.appendChild(
    makeSliderRow('Master', working.audio.master, (v) => {
      working.audio.master = v;
      audioLivePreview();
    }),
  );
  audioPane.appendChild(
    makeSliderRow('Music', working.audio.music, (v) => {
      working.audio.music = v;
      audioLivePreview();
    }),
  );
  audioPane.appendChild(
    makeSliderRow('SFX', working.audio.sfx, (v) => {
      working.audio.sfx = v;
      audioLivePreview();
    }),
  );
  audioPane.appendChild(
    makeSliderRow('UI', working.audio.ui, (v) => {
      working.audio.ui = v;
      audioLivePreview();
    }),
  );

  // --- Tab switching ----------------------------------------------------
  const switchTab = (which: 'gameplay' | 'audio'): void => {
    const showGameplay = which === 'gameplay';
    gameplayPane.style.display = showGameplay ? 'flex' : 'none';
    audioPane.style.display = showGameplay ? 'none' : 'flex';
    setTabActive(gameplayTab, showGameplay);
    setTabActive(audioTab, !showGameplay);
  };
  gameplayTab.addEventListener('click', () => switchTab('gameplay'));
  audioTab.addEventListener('click', () => switchTab('audio'));

  // --- Footer (Save / Cancel) ------------------------------------------
  const footer = document.createElement('div');
  Object.assign(footer.style, {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end',
    marginTop: '14px',
    paddingTop: '14px',
    borderTop: '1px solid #2A3344',
  } as Partial<CSSStyleDeclaration>);
  card.appendChild(footer);

  const cancelBtn = makeFooterButton('Cancel', false);
  cancelBtn.addEventListener('click', () => {
    // v2M8.8 — revert any live-previewed audio changes by reapplying
    // the originals. Without this, a player who drags sliders then
    // clicks Cancel would keep hearing the preview until next session.
    getAudioEngine().applyVolumes(callbacks.current.audio);
    callbacks.onCancel();
  });
  footer.appendChild(cancelBtn);

  const saveBtn = makeFooterButton('Save', true);
  saveBtn.addEventListener('click', () => {
    callbacks.onSave(working);
  });
  footer.appendChild(saveBtn);

  parent.appendChild(overlay);
  getAudioEngine().playCue('ui:panel-open');
  return {
    destroy(): void {
      getAudioEngine().playCue('ui:panel-close');
      overlay.remove();
    },
  };
}

// --- Helpers ------------------------------------------------------------

function makeSpeedRadioRow(gameplay: GameplaySettings): HTMLElement {
  const row = document.createElement('div');
  Object.assign(row.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  } as Partial<CSSStyleDeclaration>);

  const label = document.createElement('div');
  label.textContent = 'Default game speed';
  Object.assign(label.style, {
    fontSize: '13px',
    color: '#D7DEEA',
  } as Partial<CSSStyleDeclaration>);
  row.appendChild(label);

  const help = document.createElement('div');
  help.textContent = 'Sim speed when a new or loaded game starts.';
  Object.assign(help.style, {
    fontSize: '11px',
    color: '#7E8799',
  } as Partial<CSSStyleDeclaration>);
  row.appendChild(help);

  const buttons = document.createElement('div');
  Object.assign(buttons.style, {
    display: 'inline-flex',
    gap: '0',
    alignSelf: 'flex-start',
    marginTop: '4px',
  } as Partial<CSSStyleDeclaration>);
  row.appendChild(buttons);

  const btnEls: HTMLButtonElement[] = USER_SPEED_LADDER.map((s, i) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = `${s}×`;
    Object.assign(btn.style, {
      padding: '6px 16px',
      background: '#0E131C',
      color: '#D7DEEA',
      border: '1px solid #2A3344',
      borderLeft: i === 0 ? '1px solid #2A3344' : 'none',
      fontFamily: 'inherit',
      fontSize: '12px',
      letterSpacing: '0.06em',
      cursor: 'pointer',
    } as Partial<CSSStyleDeclaration>);
    if (i === 0) {
      btn.style.borderTopLeftRadius = '2px';
      btn.style.borderBottomLeftRadius = '2px';
    }
    if (i === USER_SPEED_LADDER.length - 1) {
      btn.style.borderTopRightRadius = '2px';
      btn.style.borderBottomRightRadius = '2px';
    }
    return btn;
  });

  function paint(): void {
    for (let i = 0; i < btnEls.length; i++) {
      const btn = btnEls[i]!;
      const speed: UserSpeed = USER_SPEED_LADDER[i]!;
      const active = gameplay.defaultGameSpeed === speed;
      btn.style.background = active ? '#2B4A7A' : '#0E131C';
      btn.style.color = active ? '#FFFFFF' : '#D7DEEA';
      btn.style.borderColor = active ? '#3F6FA8' : '#2A3344';
      btn.style.borderLeft = i === 0 ? btn.style.borderColor : 'none';
    }
  }

  for (let i = 0; i < btnEls.length; i++) {
    const btn = btnEls[i]!;
    const speed: UserSpeed = USER_SPEED_LADDER[i]!;
    btn.addEventListener('click', () => {
      gameplay.defaultGameSpeed = speed;
      paint();
    });
    buttons.appendChild(btn);
  }
  paint();

  return row;
}

/**
 * v3M19.3 — sub-section divider for the Settings panes. Visually
 * partitions related controls under a labelled heading without
 * introducing a full nested-tab UX. Used by the Gameplay tab's
 * Combat VFX section; can be reused by other tabs as their control
 * counts grow.
 */
function makeSectionHeader(label: string): HTMLElement {
  const wrap = document.createElement('div');
  Object.assign(wrap.style, {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginTop: '4px',
    paddingTop: '12px',
    borderTop: '1px solid #2A3344',
  } as Partial<CSSStyleDeclaration>);
  const text = document.createElement('div');
  text.textContent = label;
  Object.assign(text.style, {
    fontSize: '11px',
    letterSpacing: '0.10em',
    color: '#8EA0C8',
    textTransform: 'uppercase',
  } as Partial<CSSStyleDeclaration>);
  wrap.appendChild(text);
  return wrap;
}

function makeToggleRow(
  label: string,
  help: string,
  initial: boolean,
  onChange: (v: boolean) => void,
): HTMLElement {
  let value = initial;
  const row = document.createElement('div');
  Object.assign(row.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  } as Partial<CSSStyleDeclaration>);

  const top = document.createElement('div');
  Object.assign(top.style, {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  } as Partial<CSSStyleDeclaration>);
  row.appendChild(top);

  const labelEl = document.createElement('div');
  labelEl.textContent = label;
  Object.assign(labelEl.style, {
    flex: '1 1 auto',
    fontSize: '13px',
    color: '#D7DEEA',
  } as Partial<CSSStyleDeclaration>);
  top.appendChild(labelEl);

  const toggleBtn = document.createElement('button');
  toggleBtn.type = 'button';
  Object.assign(toggleBtn.style, {
    padding: '6px 14px',
    fontFamily: 'inherit',
    fontSize: '12px',
    letterSpacing: '0.06em',
    border: '1px solid',
    borderRadius: '2px',
    cursor: 'pointer',
    minWidth: '64px',
  } as Partial<CSSStyleDeclaration>);

  function paint(): void {
    toggleBtn.textContent = value ? 'On' : 'Off';
    toggleBtn.style.background = value ? '#2B4A7A' : '#0E131C';
    toggleBtn.style.color = value ? '#FFFFFF' : '#D7DEEA';
    toggleBtn.style.borderColor = value ? '#3F6FA8' : '#2A3344';
  }
  toggleBtn.addEventListener('click', () => {
    value = !value;
    onChange(value);
    paint();
  });
  paint();
  top.appendChild(toggleBtn);

  const helpEl = document.createElement('div');
  helpEl.textContent = help;
  Object.assign(helpEl.style, {
    fontSize: '11px',
    color: '#7E8799',
  } as Partial<CSSStyleDeclaration>);
  row.appendChild(helpEl);

  return row;
}

function makeFooterButton(label: string, primary: boolean): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = label;
  Object.assign(btn.style, {
    padding: '10px 24px',
    background: primary ? '#2B4A7A' : '#0E131C',
    color: primary ? '#FFFFFF' : '#D7DEEA',
    border: `1px solid ${primary ? '#3F6FA8' : '#2A3344'}`,
    borderRadius: '2px',
    fontFamily: 'inherit',
    fontSize: '13px',
    letterSpacing: '0.06em',
    cursor: 'pointer',
  } as Partial<CSSStyleDeclaration>);
  return btn;
}

/**
 * v2M8.8 — clickable tab pill. Active state matches the previous
 * single-tab inline styling so the visual didn't shift when Audio
 * joined.
 */
function makeTabButton(label: string, active: boolean): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = label;
  Object.assign(btn.style, {
    padding: '6px 12px',
    borderRadius: '2px',
    fontFamily: 'inherit',
    fontSize: '12px',
    letterSpacing: '0.06em',
    cursor: 'pointer',
  } as Partial<CSSStyleDeclaration>);
  setTabActive(btn, active);
  return btn;
}

function setTabActive(btn: HTMLButtonElement, active: boolean): void {
  btn.style.background = active ? '#172031' : '#0E131C';
  btn.style.border = `1px solid ${active ? '#3F4C66' : '#2A3344'}`;
  btn.style.color = active ? '#D7DEEA' : '#9FA5B3';
}

/**
 * v2M8.8 — 0..1 volume slider row. Live-callback fires on every
 * input event so the player hears the change immediately (the
 * caller calls `getAudioEngine().applyVolumes` in `onChange`).
 */
function makeSliderRow(
  label: string,
  initial: number,
  onChange: (value: number) => void,
): HTMLElement {
  const row = document.createElement('div');
  Object.assign(row.style, {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  } as Partial<CSSStyleDeclaration>);

  const lab = document.createElement('div');
  lab.textContent = label;
  Object.assign(lab.style, {
    width: '70px',
    fontSize: '13px',
    letterSpacing: '0.04em',
  } as Partial<CSSStyleDeclaration>);
  row.appendChild(lab);

  const slider = document.createElement('input');
  slider.type = 'range';
  slider.min = '0';
  slider.max = '100';
  slider.step = '1';
  slider.value = String(Math.round(initial * 100));
  slider.style.flex = '1';
  slider.dataset.noSound = ''; // suppress global ui:click on slider drag
  row.appendChild(slider);

  const readout = document.createElement('div');
  readout.textContent = `${slider.value}`;
  Object.assign(readout.style, {
    width: '36px',
    textAlign: 'right',
    fontSize: '12px',
    color: '#7E8799',
    fontVariantNumeric: 'tabular-nums',
  } as Partial<CSSStyleDeclaration>);
  row.appendChild(readout);

  slider.addEventListener('input', () => {
    const v = Number(slider.value) / 100;
    readout.textContent = slider.value;
    onChange(v);
  });

  return row;
}
